(() => {
  if (window.OswaalMenuPanels) return;

  const requests = new Map();
  const sectionName = 'oswaal-lazy-menu-panels';
  const version = '1';

  function status(menu, panel, failed) {
    const message = document.createElement('p');
    message.className = 'omm-lazy-status';
    message.textContent = failed
      ? menu.dataset.errorText
      : menu.dataset.loadingText;

    if (!panel.hidden) menu.announce(message.textContent);

    panel.replaceChildren(message);

    if (failed) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'omm-lazy-retry';
      retry.setAttribute('data-omm-retry', '');
      retry.setAttribute('data-omm-toggle', '');
      retry.textContent = menu.dataset.retryText;
      panel.append(retry);
    }
  }

  function payload(menu) {
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionName);

    const expected = {
      root: menu.dataset.root,
      signature: menu.dataset.signature,
      theme: menu.dataset.theme,
      language: menu.dataset.language,
      country: menu.dataset.country,
      version
    };

    const key = url.href + '|' + JSON.stringify(expected);

    if (requests.has(key)) return requests.get(key);

    const promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(url.href, {
          credentials: 'same-origin',
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error('Menu HTTP ' + response.status);
        }

        const json = await response.json();
        const html = json[sectionName];

        if (typeof html !== 'string' || !html.trim()) {
          throw new Error('Empty menu section');
        }

        const doc = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const source = doc.querySelector('[data-omm-response]');

        if (!source) {
          throw new Error('Menu response not found');
        }

        for (const [name, value] of Object.entries(expected)) {
          if (source.dataset[name] !== value) {
            throw new Error('Menu context changed: ' + name);
          }
        }

        const template = source.querySelector(
          'template[data-omm-payload]'
        );

        if (!template) {
          throw new Error('Menu payload not found');
        }

        return { template, key, promise };
      } finally {
        clearTimeout(timeout);
      }
    })();

    requests.set(key, promise);
    promise.catch(() => requests.delete(key));

    return promise;
  }

  async function ensure(menu, item) {
    const panel = item.querySelector(':scope > .omm-children');

    if (
      !panel ||
      !panel.hasAttribute('data-omm-lazy') ||
      panel.dataset.loadState === 'ready'
    ) {
      return true;
    }

    if (panel.ommPromise) return panel.ommPromise;

    panel.dataset.loadState = 'loading';
    panel.setAttribute('aria-busy', 'true');
    status(menu, panel, false);

    panel.ommPromise = (async () => {
      let reply;

      try {
        reply = await payload(menu);
        const template = reply.template;

        if (!menu.isConnected || !panel.isConnected) {
          return false;
        }

        const sourceItem = Array.from(
          template.content.querySelectorAll(
            '.omm-level-1 > .omm-item'
          )
        ).find(candidate => {
          return candidate.dataset.ommKey === item.dataset.ommKey;
        });

        const sourcePanel = sourceItem?.querySelector(
          ':scope > .omm-children'
        );

        if (!sourcePanel?.id) {
          throw new Error('Requested menu branch is missing');
        }

        const fragment = document.createDocumentFragment();

        Array.from(sourcePanel.childNodes).forEach(node => {
          fragment.append(node.cloneNode(true));
        });

        const remap = id => {
          if (
            id !== sourcePanel.id &&
            !id.startsWith(sourcePanel.id + '-')
          ) {
            throw new Error('Invalid menu ID namespace');
          }

          return panel.id + id.slice(sourcePanel.id.length);
        };

        const ids = new Set();

        fragment.querySelectorAll('[id]').forEach(node => {
          const id = remap(node.id);

          if (ids.has(id) || document.getElementById(id)) {
            throw new Error('Duplicate menu ID');
          }

          ids.add(id);
          node.id = id;
        });

        fragment.querySelectorAll('[aria-controls]').forEach(node => {
          const id = remap(node.getAttribute('aria-controls'));

          if (!ids.has(id)) {
            throw new Error('Missing controlled menu panel');
          }

          node.setAttribute('aria-controls', id);
        });

        panel.replaceChildren(fragment);
        panel.dataset.loadState = 'ready';

        if (!panel.hidden) menu.announce(menu.dataset.readyText);

        return true;
      } catch (error) {
        if (
          reply &&
          requests.get(reply.key) === reply.promise
        ) {
          requests.delete(reply.key);
        }

        if (menu.isConnected && panel.isConnected) {
          panel.dataset.loadState = 'error';
          status(menu, panel, true);
        }

        console.warn('[Oswaal menu]', error.message);
        return false;
      } finally {
        panel.removeAttribute('aria-busy');
        panel.ommPromise = null;
      }
    })();

    return panel.ommPromise;
  }

  window.OswaalMenuPanels = { ensure };
})();

if (!customElements.get('oswaal-menu')) {
  customElements.define('oswaal-menu', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;

      this.events = new AbortController();
      const options = { signal: this.events.signal };

      this.addEventListener('click', event => {
        const retry = event.target.closest('[data-omm-retry]');

        if (retry && this.contains(retry)) {
          const item = retry.closest('[data-depth="1"]');
          this.button(item)?.focus();
          this.expand(item, true);
          return;
        }

        const button = event.target.closest('[data-omm-toggle]');

        if (!button || !this.contains(button)) return;

        this.expand(
          button.closest('.omm-item'),
          button.getAttribute('aria-expanded') !== 'true'
        );
      }, options);

      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;

        let item = event.target.closest('.omm-item');
        let openItem = null;

        while (item && this.contains(item)) {
          if (
            this.button(item)?.getAttribute('aria-expanded') === 'true'
          ) {
            openItem = item;
            break;
          }

          item = item.parentElement.closest('.omm-item');
        }

        if (!openItem && this.dataset.mode === 'drawer') {
          const buttons = this.querySelectorAll(
            '[data-omm-toggle][aria-expanded="true"]'
          );

          openItem = buttons[buttons.length - 1]?.closest('.omm-item');
        }

        if (!openItem) return;

        this.skipEscapeUp = true;
        event.preventDefault();
        event.stopPropagation();

        const button = this.button(openItem);
        this.expand(openItem, false);
        button.focus();
      }, options);

      this.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.skipEscapeUp) {
          event.stopPropagation();
          this.skipEscapeUp = false;
        }
      }, options);

      this.breakpoint = matchMedia('(min-width: 990px)');

      this.breakpoint.addEventListener('change', () => {
        this.closeAll();

        const drawer = this.closest('header-drawer');

        if (
          drawer?.mainDetailsToggle?.open &&
          drawer.dataset.breakpoint !== 'desktop'
        ) {
          drawer.closeMenuDrawer(
            new Event('resize'),
            drawer.mainDetailsToggle.querySelector('summary')
          );
        }
      }, options);

      const outer = this.closest('header-drawer')?.querySelector(
        'details'
      );

      outer?.addEventListener('toggle', () => {
        if (!outer.open) this.closeAll();
      }, options);

      if (this.dataset.mode !== 'desktop') return;

      this.addEventListener('pointerover', event => {
        if (
          event.pointerType === 'touch' ||
          !matchMedia('(hover:hover) and (pointer:fine)').matches
        ) {
          return;
        }

        const row = event.target.closest('.omm-row');

        if (row && row.contains(event.relatedTarget)) return;

        clearTimeout(this.categoryHoverTimer);

        if (!row || !this.contains(row)) return;

        const item = row.closest('.omm-item');
        if (!item) return;

        const depth = Number(item.dataset.depth);

        if (depth === 2) {
          this.categoryHoverTimer = setTimeout(() => {
            if (this.isConnected && row.matches(':hover')) {
              this.selectCategory(item);
            }
          }, 300);
        } else if ((depth === 1 || depth === 3) && this.button(item)) {
          this.expand(item, true);
        }
      }, options);

      this.addEventListener('pointerout', event => {
        const row = event.target.closest('.omm-row');

        if (row && !row.contains(event.relatedTarget)) {
          clearTimeout(this.categoryHoverTimer);
        }
      }, options);

      this.addEventListener('pointerleave', () => {
        clearTimeout(this.categoryHoverTimer);

        if (!this.contains(document.activeElement)) {
          this.closeAll();
        }
      }, options);

      this.addEventListener('focusin', event => {
        if (!event.target.matches('a')) return;

        const root = event.target.closest('[data-depth="1"]');
        const category = event.target.closest('[data-depth="2"]');
        const classItem = event.target.closest('[data-depth="3"]');

        if (category) {
          this.selectCategory(category);
          if (classItem && this.button(classItem)) {
            this.expand(classItem, true);
          }
        } else if (root && this.button(root)) {
          this.expand(root, true);
        }
      }, options);

      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (
            !this.contains(document.activeElement) &&
            !this.matches(':hover')
          ) {
            this.closeAll();
          }
        });
      }, options);
    }

    button(item) {
      if (!item) return null;

      return item.querySelector(
        ':scope > .omm-row > [data-omm-toggle]'
      );
    }

    panel(item) {
      return item.querySelector(':scope > .omm-children');
    }

    announce(message) {
      const announcer = this.querySelector('[data-omm-announcer]');
      if (!announcer) return;

      cancelAnimationFrame(this.announceFrame);
      announcer.textContent = '';

      this.announceFrame = requestAnimationFrame(() => {
        if (this.isConnected) announcer.textContent = message;
      });
    }

    selectCategory(item) {
      Array.from(item.parentElement.children).forEach(sibling => {
        this.setState(sibling, sibling === item);
      });
    }

    setState(item, open) {
      const panel = this.panel(item);
      const button = this.button(item);

      if (!panel || !button) return;

      if (!open) {
        item.ommIntent = (item.ommIntent || 0) + 1;

        item.querySelectorAll('.omm-item').forEach(child => {
          child.ommIntent = (child.ommIntent || 0) + 1;
          const childPanel = this.panel(child);
          const childButton = this.button(child);

          if (childPanel) childPanel.hidden = true;
          childButton?.setAttribute('aria-expanded', 'false');
        });
      }

      panel.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
    }

    async expand(item, open) {
      if (!item) return;

      const depth = Number(item.dataset.depth);
      const panel = this.panel(item);
      const button = this.button(item);

      if (!panel || !button) return;

      const intent = item.ommIntent = (item.ommIntent || 0) + 1;

      clearTimeout(this.categoryHoverTimer);

      if (
        this.dataset.mode === 'desktop' &&
        open &&
        depth <= 3
      ) {
        Array.from(item.parentElement.children).forEach(sibling => {
          if (sibling !== item) this.setState(sibling, false);
        });
      }

      if (
        this.dataset.mode === 'desktop' &&
        open &&
        depth === 1
      ) {
        const header = this.closest('.header-wrapper');

        if (header) {
          this.style.setProperty(
            '--omm-header-bottom',
            `${Math.max(
              0,
              Math.ceil(header.getBoundingClientRect().bottom)
            )}px`
          );
        }
      }

      this.setState(item, open);
      this.refresh();

      if (!open) return;

      let loaded = true;

      if (panel.hasAttribute('data-omm-lazy')) {
        if (window.OswaalMenuPanels) {
          loaded = await window.OswaalMenuPanels.ensure(this, item);
        } else {
          panel.textContent = this.dataset.errorText;
          loaded = false;
        }

        if (
          !this.isConnected ||
          item.ommIntent !== intent ||
          button.getAttribute('aria-expanded') !== 'true'
        ) {
          return;
        }
      }

      if (
        loaded &&
        this.dataset.mode === 'desktop' &&
        depth === 1
      ) {
        const firstCategory = panel.querySelector(
          '.omm-level-2 > .omm-item'
        );

        if (firstCategory) this.selectCategory(firstCategory);
      }

      this.refresh();
    }

    refresh() {
      const outer = this.closest('header-drawer')?.querySelector(
        'details'
      );

      if (
        outer?.open &&
        outer.contains(document.activeElement) &&
        typeof trapFocus === 'function'
      ) {
        trapFocus(outer, document.activeElement);
      }

      if (this.dataset.mode !== 'desktop') return;

      const open = !!this.querySelector(
        '.omm-level-1 > .omm-item > .omm-row > [aria-expanded="true"]'
      );

      const sticky = this.closest('sticky-header');

      if (sticky) sticky.preventHide = open;

      if (open && !this.outside) {
        this.outside = new AbortController();

        document.addEventListener('pointerdown', event => {
          if (!this.contains(event.target)) this.closeAll();
        }, {
          capture: true,
          signal: this.outside.signal
        });
      } else if (!open) {
        this.outside?.abort();
        this.outside = null;
      }
    }

    closeAll() {
      clearTimeout(this.categoryHoverTimer);
      cancelAnimationFrame(this.announceFrame);

      this.querySelectorAll(
        '.omm-level-1 > .omm-item'
      ).forEach(item => {
        this.setState(item, false);
      });

      this.refresh();
    }

    disconnectedCallback() {
      this.closeAll();

      const drawer = this.closest('header-drawer');

      if (drawer?.mainDetailsToggle?.open) {
        drawer.closeMenuDrawer(
          new Event('section-unload'),
          drawer.mainDetailsToggle.querySelector('summary')
        );
      }

      this.events?.abort();
      this.outside?.abort();
      this.events = null;
      this.outside = null;
    }
  });
}
